import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertService } from 'src/app/services/alert-service.service';
import { PortalUrlService } from 'src/app/services/portal-url.service/portal-url.service';
import { FileItemTypeEnum, FileTypes, ImageContextTypes, } from 'src/app/shared/enums/files/files.enum';
import {
  EstimateApiStatusEmitter,
  EstimateVisibility,
  UpdatePortalEstimateDTO,
  UpdateSelectionOptionDTO,
} from 'src/app/shared/interfaces/estimate/estimate.interface';
import { Files } from 'src/app/shared/interfaces/files.interface';
import { PortalUrlContentDetailsModal } from 'src/app/shared/modals/portal-url-content-details.modal';
import {
  DetailsToIncludeEnum,
  EstimateAcceptedRejectedRevisionBy,
  EstimateDetailTypeEnum,
  EstimateSelectionTypeEnum,
  EstimateStatusTypes,
  isEstimateNoLongerALiveOffer,
  TypeOfCalculationEnum,
} from '../../shared/enums/estimate/estimate.enum';
import {
  EstimateExpiryDisplay,
  getEstimateExpiryDisplay,
} from '../../shared/utils/estimate-expiry-display';
import { resolveEstimateVisibility } from '../../shared/utils/estimate-visibility';
import { estimateTaxesPerLine, invoiceTaxesPerLine } from '../../shared/utils/estimate-tax-mode';
import { resolveDocumentHeader } from '../../shared/utils/estimate-document-header';
import { LOGO_SIZE_DEFAULT, resolveLogoSize } from '../../shared/utils/estimate-logo-size';
import { hasPrintableAddress } from '../../shared/utils/address-printable';
import {
  EstimateCustomerData,
  EstimateDetailsEntityDTO,
  EstimateDetailsSectionEntityDTO,
  EstimateEntityDTO,
  EstimateProjectData,
  EstimateSectionDisplayRenderType,
} from '../../shared/modals/estimate.modal';
import { EstimatesService } from '../../services/estimate-service/estimate.service';
import { InvoiceService } from '../../services/invoice-service/invoice.service';
import { EstimateDisplayListPipe } from "../../shared/pipes/estimate-display-list.pipe";
import { cloneDeep, orderBy, sortBy } from "lodash";
import { concatFilePath, getCustomerNameWithCompany, getCustomerNameWithCompanyForEstInv, getPhotoDisplayUrl, markThumbnailFailed, NumberUtil, objectIsEmpty } from 'src/app/shared/utils/util-function';
import { settingsProp } from 'src/app/shared/interfaces/settings/setting.interface';
import { SettingService } from 'src/app/services/setting-service/setting.service';
import { parsePhoneNumber } from 'libphonenumber-js';
import { LogRocketService } from 'src/app/services/logrocket-service/logrocket.service';
import * as _ from 'lodash';
import { debounceTime, Subject } from 'rxjs';
import { GlobalStateService } from 'src/app/services/global-state.service';
import { PUBLISH_STATUSES, APPROVED_STATUSES } from 'src/app/shared/enums/invoice/invoice.enum';

@Component({
  selector: 'projul-estimate',
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.scss'],
})
export class EstimateComponent implements OnInit, OnChanges {
  @ViewChild('iframe', { static: true }) printContent: any;
  @Input() portalEstimate: PortalUrlContentDetailsModal;
  /** A signed-in user answering for the customer, as verified by the server for this link. */
  get isPreview(): boolean {
    return !!this.portalEstimate?.IsPreview;
  }

  /** Options stop changing once the estimate is approved, paid or closed; the server refuses a change then too. */
  get selectionsLocked(): boolean {
    return [EstimateStatusTypes.Approved, EstimateStatusTypes.CompletelyPaid, EstimateStatusTypes.Closed].includes(this.status);
  }
  typeText = '';
  actionText: string = '';
  spinnerLabel: string = '';
  todayDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date());
  showAcceptAndSign: boolean = false;
  showRequestRevision: boolean = false;
  showReject: boolean = false;
  isClosed: boolean = false;
  isCollapsed: boolean = false;
  showPrintPreview: boolean = false;
  isDueDate: boolean = false;
  nameError: boolean = false;
  showLoader: boolean = false;
  apiSuccessResponse: boolean = false;
  filesList: Files[] = [];
  photosList: Files[] = [];
  companyLogo: Files;
  estimateDetailTypeEnum = EstimateDetailTypeEnum;
  status: EstimateStatusTypes;
  typeOfCalculationEnum = TypeOfCalculationEnum;
  estimateStatusTypes = EstimateStatusTypes;
  estimate: EstimateEntityDTO;
  estimateDetails: EstimateDetailsEntityDTO;
  renderTypes = EstimateSectionDisplayRenderType;
  isAllQuantityViewAble: boolean = true;
  acceptedRejectedRevisionBy = EstimateAcceptedRejectedRevisionBy;
  customerPlaceHolderData: EstimateCustomerData;
  projectPlaceHolderData: EstimateProjectData;
  estimateSettings = { ...settingsProp.estimateSettings };
  isLoading: boolean = true;
  isErrorFromApi: boolean = false;
  newlyStatusUpdated = false;
  estimateCrossedOutTotal: number = 0;
  /** The document's stored tax mode; the account's setting never decides how a shared estimate prices. */
  taxesPerLine = false;
  /**
   * Which client-facing fields the contractor left switched on, shared by the base estimate and
   * every change order. Seeded all-shown so the first render never hides anything.
   */
  visibility: EstimateVisibility = resolveEstimateVisibility(undefined);

  /** Resolved alongside the header rather than read in the template, which re-runs it every tick. */
  logoSizeClass: string = resolveLogoSize(LOGO_SIZE_DEFAULT).sizeClass;


  requestRevisionForm: FormGroup = new FormGroup({
    FullName: new FormControl('', Validators.required),
    Reason: new FormControl('', Validators.required),
    Status: new FormControl(EstimateStatusTypes.RevisionRequested),
  });

  rejectForm: FormGroup = new FormGroup({
    FullName: new FormControl('', Validators.required),
    Reason: new FormControl('', Validators.required),
    Status: new FormControl(EstimateStatusTypes.Rejected),
  });

  estimateSelectionType = EstimateSelectionTypeEnum;
  private updateOptionsSubject: Subject<any> = new Subject<any>();
  DefaultPrintedPhotoSize: number;
  photoThumbUrl = getPhotoDisplayUrl;
  onThumbError = markThumbnailFailed;
  hasPrintableAddress = hasPrintableAddress;

  /**
   * The client-facing header's status and date line: the approval date once the client has
   * accepted the document (or it is paid or closed), the expiry before that.
   */
  get expiryDisplay(): EstimateExpiryDisplay | null {
    if (this.estimate?.IsTemplate) return null;
    return getEstimateExpiryDisplay(this.estimateDetails, this.documentAlias, this.status);
  }

  /** "Change Order" for a change order, otherwise the tenant's own estimate/contract wording. */
  get documentAlias(): string {
    return this.estimateDetails?.Name === 'Change Order'
      ? this.estimateDetails.Name
      : this.estimate?.EstimateAlias;
  }

  /** True once the document is accepted/paid/closed, so it can no longer be called expired. */
  get isNoLongerALiveOffer(): boolean {
    return isEstimateNoLongerALiveOffer(this.status);
  }

  get projectNumberText(): string {
    return this.estimate?.ProjectNumber || this.projectPlaceHolderData?.ProjectNumber || '';
  }

  get projectNameText(): string {
    return this.estimate?.ProjectName || this.projectPlaceHolderData?.ProjectName || '';
  }

  /**
   * The header's project line is two independently hideable halves, so each half owns its own
   * emptiness check and the " - " joiner only appears when both halves are actually rendered.
   */
  get showProjectNumberText(): boolean {
    return this.visibility.ShowProjectNumber && !!this.projectNumberText;
  }

  get showProjectNameText(): boolean {
    return this.visibility.ShowProjectName && !!this.projectNameText;
  }

  constructor(
    private portalUrlService: PortalUrlService,
    private alertService: AlertService,
    private estimateService: EstimatesService,
    private settingService: SettingService,
    private estimateDisplayListPipe: EstimateDisplayListPipe,
    private logRocketService: LogRocketService,
    private globalStateService: GlobalStateService,
    private invoiceService: InvoiceService
  ) {
    this.updateOptionsSubject.pipe(debounceTime(2000)).subscribe(async action => await this.submitOpdateOptions(action));
  }

  get revisionControls() {
    return this.requestRevisionForm.controls;
  }

  get rejectControls() {
    return this.rejectForm.controls;
  }

  // Set once settings finish loading, so ngOnChanges knows the first input change is already
  // in ngOnInit's hands and must not race it.
  private estimateSettingsLoaded = false;
  // Tracks the most recent re-derivation triggered by an input change, so a slower
  // earlier refetch can't stomp on a faster later one when their awaits resolve out of order.
  private latestApplyToken = 0;

  async ngOnInit() {

    let settingList = await this.settingService.getSettingList(this.portalEstimate?.CompanyId);
    this.estimateSettings = this.settingService.mapSettingValues(settingList, this.estimateSettings);
    this.estimateSettingsLoaded = true;

    this.globalStateService.checkOutDatedDataObservable.pipe().subscribe(async (res: any) => {
      let updatedDate = (await this.portalUrlService.getUpdatedDateOfEntity(this.portalEstimate.UrlId))?.Response as any;
      if (updatedDate != null && this.estimateDetails && this.estimateDetails.UpdatedDate < updatedDate) {
        this.globalStateService.setClientPortalRelatedDataChanges();
      }
    });

    await this.applyPortalEstimate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-derive from the new @Input on later updates, such as a SignalR refetch. The first change
    // belongs to ngOnInit, which must load settings before deriving.
    const change = changes['portalEstimate'];
    if (change && !change.firstChange && this.estimateSettingsLoaded) {
      // Fire-and-forget; applyPortalEstimate guards against out-of-order completions.
      void this.applyPortalEstimate();
    }
  }

  // The document still renders, but on the parent's visibility, which can show a field this
  // document chose to hide. Only the stored blob explains why it could not be read.
  private reportUnreadableHeaderOverride(estimateId: string, serialised: string, error: unknown): void {
    this.logRocketService.captureMessage('Unreadable estimate header override; fell back to the parent estimate header.', {
      tags: { feature: 'estimate-header', severity: 'warning' },
      extra: {
        estimateId,
        overrideLength: serialised.length,
        overrideStart: serialised.slice(0, 500),
        error: error instanceof Error ? error.message : String(error)
      }
    });
  }

  private async applyPortalEstimate() {
    const token = ++this.latestApplyToken;
    // Publish only after mapEstimateSection runs: the template is still bound to the previous,
    // fully populated values, and the raw payload has no EstimateValues for those bindings.
    const incomingItem = this.portalEstimate.Item as EstimateEntityDTO;
    console.log('Opened Estimate with Id:', incomingItem?.Id)
    const taxesPerLine = estimateTaxesPerLine(incomingItem);

    let mapValues = await this.estimateService.mapEstimateSection(this.portalEstimate.Item, this.portalEstimate.Item.Taxes || [], taxesPerLine);
    if (token !== this.latestApplyToken) return;
    incomingItem.EstimateValues = mapValues.estimateValues;
    incomingItem.EstimateDetails = mapValues.estimateDetails;
    // The backend filters to the ONE requested document, so its header is resolved onto the estimate
    // here and every `estimate.CompanyName`-style binding keeps reading the right values unchanged.
    Object.assign(incomingItem, resolveDocumentHeader(
      incomingItem,
      mapValues.estimateDetails?.[0],
      (serialised, error) => this.reportUnreadableHeaderOverride(incomingItem?.Id, serialised, error)
    ));
    this.visibility = resolveEstimateVisibility(incomingItem);
    this.logoSizeClass = resolveLogoSize(incomingItem.LogoSize ?? LOGO_SIZE_DEFAULT).sizeClass;
    // Publish atomically — both have their critical derived fields set so the
    // template can safely re-render against the new objects.
    this.estimate = incomingItem;
    this.taxesPerLine = taxesPerLine;
    this.estimateDetails = mapValues.estimateDetails[0];

    // Use TotalWithTaxBeforeDiscount from calculated values for crossed out total
    this.estimateCrossedOutTotal = this.estimate.EstimateValues?.TotalWithTaxBeforeDiscount || 0;

    // Calculate and cache rates for all costlines
    this.calculateRates();
    
    if (!this.estimate?.Name ?? '') {
      this.estimate.Name = this.estimateSettings?.EstimateAliasName ?? '';
    }
    this.estimate.Discounts = this.estimateDetails?.EstimateDiscounts || [];

    if (this.estimateDetails.EstimateType == EstimateDetailTypeEnum.ChangeOrder) {
      this.estimate.EstimateDetailsList ||= [];
      let estimateDetails = sortBy(this.estimate.EstimateDetailsList, (y) => y.CreatedDate, 'desc');
      let edIndex = estimateDetails.findIndex(x => x.Id == this.estimateDetails.Id);
      let currentCODate: Date | string = this.tryGetISODate(this.estimateDetails.EstimateDate);

      // Filter estimate details created before the current change order
      // Use string comparison to preserve microsecond precision
      let previousEstimateDetails = estimateDetails.filter((x, i) => {
        if (edIndex <= i) {
          return false;
        }

        // The parent estimate is the contract's origin, so it precedes every change order
        // even when the change order carries an earlier, user-entered document date.
        if (x.EstimateType !== EstimateDetailTypeEnum.ChangeOrder) {
          return true;
        }

        // For ISO date strings, direct string comparison preserves full precision
        // and works correctly for chronological ordering
        return this.tryGetISODate(x.EstimateDate) < currentCODate;
      });

      let estimateEntity = { ...this.portalEstimate.Item }
      estimateEntity.EstimateDetails = previousEstimateDetails;
      const calculatedValues = await this.estimateService.mapEstimateSection(estimateEntity, this.portalEstimate.Item.Taxes || [], taxesPerLine);
      if (token !== this.latestApplyToken) return;
      previousEstimateDetails = calculatedValues.estimateDetails;


      // Include only published non-rejected estimate details for calculation
      let publishedPreviousDetails = previousEstimateDetails.filter(x => {
        // Include only truly published estimates (approved, pending approval, overdue, or revision requested)
        // Draft, PendingReview, PendingSend, ReviewRevisionRequested are all considered unpublished
        let isPublished = [EstimateStatusTypes.Approved, EstimateStatusTypes.PendingApproval, EstimateStatusTypes.Overdue, EstimateStatusTypes.RevisionRequested, EstimateStatusTypes.CompletelyPaid].includes(x.Status);
        // Exclude rejected documents
        let isNotRejected = x.Status != EstimateStatusTypes.Rejected;

        return isPublished && isNotRejected;
      });

      // Get invoices if available
      let invoices = this.estimate.Invoices || this.portalEstimate.Item?.Invoices || [];
      let invoiceContribution = 0;

      if (invoices && invoices.length > 0) {
        // Filter published invoices created before the current change order
        let relevantInvoices = invoices.filter(inv => {
          // Must be created before current CO
          // Use string comparison to preserve microsecond precision
          if (this.tryGetISODate(inv.InvoiceDate) >= currentCODate) {
            return false;
          }

          // Include only published invoices (not draft or rejected)
          let isPublished = PUBLISH_STATUSES.includes(inv.Status) || APPROVED_STATUSES.includes(inv.Status);
          return isPublished;
        });

        // Calculate contribution from invoices
        invoiceContribution = await this.calculateInvoiceContribution(relevantInvoices, publishedPreviousDetails, previousEstimateDetails);
        if (token !== this.latestApplyToken) return;
      }

      let previousEstimateValues = this.estimateService.getEstimateCalculatedValues(publishedPreviousDetails, this.portalEstimate.Item.Taxes || [], DetailsToIncludeEnum.All, [], taxesPerLine);

      // Calculate Previous Project Total (includes published estimates + qualifying invoice values)
      this.estimateDetails.PreviousTotal = (previousEstimateValues.TotalWithTax || 0) - (previousEstimateValues.TotalDiscount || 0) + invoiceContribution;

      // Calculate Revised Project Total = Previous Total + current CO value
      // Always include the current CO's value when viewing it (even if rejected, as it shows what the total would be)
      let currentCOValue = (this.estimate.EstimateValues.TotalWithTax || 0) - (this.estimate.EstimateValues.TotalDiscount || 0);
      this.estimateDetails.RevisedTotal = (this.estimateDetails.PreviousTotal || 0) + currentCOValue;
    }

    if (this.estimateDetails.EstimateType === this.estimateDetailTypeEnum.Normal && !this.estimate.Name) {
      this.typeText = this.estimate.EstimateAlias;
    } else if (this.estimateDetails.EstimateType === this.estimateDetailTypeEnum.Normal && this.estimate.Name) {
      this.typeText = this.estimate.Name;
    } else if (this.estimateDetails.EstimateType === this.estimateDetailTypeEnum.ChangeOrder && this.estimate.Name) {
      this.typeText = this.estimate.Name;
    } else {
      this.typeText = 'Change Order';
    }
    this.status = this.estimateDetails.Status;

    this.isDueDate = new Date() > new Date(this.estimateDetails.EstimateExpiresDate);
    if (this.estimateDetails.CustomerCanApproveAfterExpirationDate) {
      this.isDueDate = false;
    }

    //placeholders
    this.customerPlaceHolderData = this.estimate?.CustomerId && this.estimate.Customer ? ([this.estimate.Customer].map(c => {
      return {
        CustomerId: c.Id,
        CompanyEmail: c.Emails?.length ? c.Emails[0].Email : '',
        CustomerAddress: c.Address ? {
          City: c.Address.City,
          PostalCode: c.Address.PostalCode,
          State: c.Address.State,
          Street1: c.Address.Street1,
          Latitude: c.Address.Latitude,
          Longitude: c.Address.Longitude
        } : {},
        CustomerFirstName: c.FirstName,
        CustomerLastName: c.LastName,
        CustomerPhone: c.PhoneNumbers?.length ? c.PhoneNumbers[0].PhoneNumber : '',
        CompanyName: c.CompanyName
      } as EstimateCustomerData;
    })[0]) : {} as EstimateCustomerData;

    this.projectPlaceHolderData = this.estimate?.ProjectId && this.estimate.ProjectV2Entities ? ([this.estimate.ProjectV2Entities].map(p => {
      return {
        ProjectId: p.Id,
        ProjectName: p.Name || p.DisplayName,
        ProjectNumber: p.ProjectNumber,
        ProjectAddress: p.Address ? {
          City: p.Address.City,
          PostalCode: p.Address.PostalCode,
          State: p.Address.State,
          Street1: p.Address.Street1,
          Latitude: p.Address.Latitude,
          Longitude: p.Address.Longitude
        } : {},
        PhoneNo: p.ProjectPhoneNumbers?.length ? p.ProjectPhoneNumbers[0].PhoneNumber : ''
      } as EstimateProjectData;
    })[0]) : {} as EstimateProjectData;

    this.estimate.Files = this.estimate.Files || [];

    let companyLogoList: any[] = this.estimate.Files.filter(
      (x) =>
        x.ImageContextType === ImageContextTypes.Settings
    );

    this.companyLogo = companyLogoList.length > 0 ? companyLogoList[0] : null;

    this.estimate.Files = this.estimate.Files.filter(f => f.Id != this.companyLogo?.Id)

    this.photosList = this.estimate.Files.filter(
      (x) =>
        x.FileType === FileTypes.Image &&
        x.ImageContextType === ImageContextTypes.Gallery
        && x.FileItemType == FileItemTypeEnum.Estimate
    );
    this.filesList = this.estimate.Files.filter(
      (x) =>
        x.FileType === FileTypes.Document &&
        x.ImageContextType === ImageContextTypes.Gallery
    );
    (this.filesList || []).forEach(o => {
      if (!!o.FileName)
        o.FileNameURI = encodeURIComponent(o.FileName);
      while (o.FileNameURI?.indexOf('#') > -1)
        o.FileNameURI = (o.FileNameURI).replace('#', '%23');
    })

    this.buttonStateMaintain(this.status);

    //set modified from section details
    (this.estimateDetails.EstimateDetailsSections || []).filter(x => x.ModifiedFromSectionId).forEach(s => {
      let details = (this.estimate.EstimateDetailsList || []).filter((d) => d.EstimateDetailsSections.some((q) => q.Id === s.ModifiedFromSectionId)).map(x => {
        return { ModifiedFromSectionDetailsNumber: x.EstimateNumber, ModifiedFromSectionDetailsType: x.EstimateType }
      })[0];
      if (details) {
        s.ModifiedFromSectionDetailsNumber = details.ModifiedFromSectionDetailsNumber;
        s.ModifiedFromSectionDetailsType = details.ModifiedFromSectionDetailsType;
      }
    });
    let estimateCostLines = this.estimateService.flatEstimateCostlines(this.estimateDetails.EstimateDetailsSections.filter(x => !x.IsDisplayOnly));
    this.isAllQuantityViewAble = estimateCostLines.some(x => (this.estimate.IsItemized ? x.QtyIsVisibleItemized : x.QtyIsVisible));

    (this.estimateDetails.EstimateDetailsSections || []).forEach(s => {
      s.Total = NumberUtil.ToNumber(this.estimateService.calculateEstimateSectionTotal(s));
      this.sortSubsectionsItems(s);
      this.sortLineItemImages(s);
      s.displayList = this.estimateDisplayListPipe.transform(s);
      if (s.IsSelectionEnabled && s.SelectionType == this.estimateSelectionType.OptionalMultipleSelection) {
        s.SelectionSomeSelect = s.displayList.some(item => item.IsSelectedForSelection);
        s.SelectionAllSelect = s.displayList.every(item => item.IsSelectedForSelection);
      }
      else if (s.IsSelectionEnabled && s.SelectionType == this.estimateSelectionType.OptionalSingleSelection) {
        s.NoneSelected = !s.displayList.some(item => item.IsSelectedForSelection);
      }
      else if (!s.IsSelectionEnabled) {
        s.displayList.forEach((before, index) => {
          if (before.renderedType === 1 && index > 0) {
            s.displayList[index - 1].beforesub = true;
          }
        })
      }

      // Calculate and cache rate for each costline in this section
      this.cacheRatesForSection(s);

      (s.displayList || []).forEach((h, index) => {
        h.isPreviousSubsection = false;
        h.isNextCostline = false;
        h.isNextSubsection = false;

        if (!s.IsSelectionEnabled) {
          if (index > 0 && s.displayList[index - 1] && s.displayList[index - 1].renderedType === this.renderTypes.subsection) {
            h.isPreviousSubsection = true;
          }
          if (h.renderedType === this.renderTypes.subsection && (index + 1) <= s.displayList.length && s.displayList[index + 1] && s.displayList[index + 1].renderedType === this.renderTypes.costline) {
            h.isNextCostline = true;
          }
          if ((index + 1) <= s.displayList.length && s.displayList[index + 1] && s.displayList[index + 1].renderedType === this.renderTypes.subsection) {
            h.isNextSubsection = true;
          }
        }
        else {
          h.isPreviousSubsection = (index > 0 && this.checkIfPreviousIsSubsection(index, s.displayList));
          h.isNextCostline = (h.renderedType === this.renderTypes.subsection && (index + 1) <= s.displayList.length && this.checkNextIsSectionOrCostline(index, s.displayList, this.renderTypes.costline));
          h.isNextSubsection = ((index + 1) <= s.displayList.length && this.checkNextIsSectionOrCostline(index, s.displayList, this.renderTypes.subsection));
        }
      });
    });

    this.isLoading = false;
    this.logStackDataInSessionStack();
    this.reOrderSectionsWithSelection();

    this.DefaultPrintedPhotoSize = this.estimate.DefaultPrintedPhotoSize > 0 ? this.estimate.DefaultPrintedPhotoSize : (this.estimateSettings.EstimateDefaultPrintedPhotoSize || 1);
  }

  concatFilePath(baseUri: any, fileName: any) {
    return concatFilePath(baseUri, fileName);
  }

  sortLineItemImages(s: EstimateDetailsSectionEntityDTO) {

    if (!s.IsDisplayOnly && (this.estimate.ItemsFiles || []).length > 0) {
      if (this.estimate.IsItemized) {
        (s.EstimateCostLineItems || []).forEach(f => f.ItemsFiles = this.estimate.ItemsFiles.filter(h => h.FileItemId == f.Id).map((g: any) => g));
        (s.SubEstimateDetails || []).forEach(h => (h.EstimateCostLineItems || []).forEach(f => f.ItemsFiles = this.estimate.ItemsFiles.filter(t => t.FileItemId == f.Id).map((g: any) => g)));
      }
      else {
        if (!s.IsSelectionEnabled) {
          s.ItemsFiles = [];
          (s.EstimateCostLineItems || []).forEach(f => this.estimate.ItemsFiles.filter(t => (t.FileItemId == f.Id && f.IsViewable)).map((g: any) => s.ItemsFiles.push(g)));
          (s.SubEstimateDetails || []).forEach(h => (h.EstimateCostLineItems || []).forEach(f => this.estimate.ItemsFiles.filter(t => (t.FileItemId == f.Id && f.IsViewable)).map((g: any) => s.ItemsFiles.push(g))));
        }
        else {
          s.ItemsFiles = [];
          (s.EstimateCostLineItems || []).forEach(h => {
            h.ItemsFiles = [];
            this.estimate.ItemsFiles.filter(t => t.FileItemId == h.Id).map(g => h.ItemsFiles.push(g));
          });
          (s.SubEstimateDetails || []).forEach(h => {
            h.ItemsFiles = [];
            (h.EstimateCostLineItems || []).forEach(f => this.estimate.ItemsFiles.filter(t => t.FileItemId == f.Id).map((g: any) => h.ItemsFiles.push(g)))
          });
        }
      }
    }

  }

  calculateTotals() {

    let estimateDetails = { ...this.estimateDetails, EstimateDetailsSections: (this.removedSections || []).length > 0 ? [...this.estimateDetails.EstimateDetailsSections.concat(this.removedSections || [])] : this.estimateDetails.EstimateDetailsSections };
    (estimateDetails.EstimateDetailsSections || []).forEach(s => {
      s.Total = NumberUtil.ToNumber(this.estimateService.calculateEstimateSectionTotal(s));
      this.sortSubsectionsItems(s);
      s.displayList = this.estimateDisplayListPipe.transform(s);
    });

    let estimateValues = this.estimateService.getEstimateCalculatedValues([estimateDetails], [], DetailsToIncludeEnum.All, [], this.taxesPerLine);
    this.estimate.EstimateValues = estimateValues;

    // Use TotalWithTaxBeforeDiscount from calculated values - this properly updates when selections change
    this.estimateCrossedOutTotal = estimateValues.TotalWithTaxBeforeDiscount || 0;

    if (this.estimateDetails.EstimateType == EstimateDetailTypeEnum.ChangeOrder) {
      // Recalculate Revised Project Total = Previous Total + current CO value
      // Always include the current CO's value when viewing it (even if rejected, as it shows what the total would be)
      let currentCOValue = (this.estimate.EstimateValues.TotalWithTax || 0) - (this.estimate.EstimateValues.TotalDiscount || 0);
      this.estimateDetails.RevisedTotal = (this.estimateDetails.PreviousTotal || 0) + currentCOValue;
    }
  }

  noneSelected(sectionIndex: number) {
    if (this.status != this.estimateStatusTypes.Approved) {
      this.unselectSectionOptions(sectionIndex);
      this.estimateDetails.EstimateDetailsSections[sectionIndex].NoneSelected = true;

      this.calculateTotals();
      this.updateOptions();
    }
  }

  toggleSelectAll(event: Event, sectionIndex: number) {
    if (this.status != this.estimateStatusTypes.Approved) {
      const target = event.target as HTMLInputElement;
      this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionAllSelect = target.checked;
      this.estimateDetails.EstimateDetailsSections[sectionIndex].EstimateCostLineItems.forEach(item => item.IsSelectedForSelection = target.checked);
      this.estimateDetails.EstimateDetailsSections[sectionIndex].SubEstimateDetails.forEach(item => item.IsSelectedForSelection = target.checked);
      this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionSomeSelect = false;

      this.calculateTotals();
      this.updateOptions();
    }
  }

  multiSelectionSteps(sectionIndex: number) {
    this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionSomeSelect = this.estimateDetails.EstimateDetailsSections[sectionIndex].displayList.some(item => item.IsSelectedForSelection);
    this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionAllSelect = this.estimateDetails.EstimateDetailsSections[sectionIndex].displayList.every(item => item.IsSelectedForSelection);
  }

  singleSelectOption(event: Event, displayIndex: number, sectionIndex: number) {
    if (this.status != this.estimateStatusTypes.Approved) {
      const target = event.target as HTMLInputElement;
      this.unselectSectionOptions(sectionIndex);
      this.selectSectionSingleOption(displayIndex, sectionIndex, target.checked);

      this.calculateTotals();
      this.updateOptions();
    }
  }

  selectSectionSingleOption(displayIndex: number, sectionIndex: number, checked: boolean) {
    let id = this.estimateDetails.EstimateDetailsSections[sectionIndex].displayList[displayIndex]?.Id;
    if (id) {
      if (this.estimateDetails.EstimateDetailsSections[sectionIndex].EstimateCostLineItems.some(f => f.Id == id)) {
        this.estimateDetails.EstimateDetailsSections[sectionIndex].EstimateCostLineItems.forEach(f => {
          if (f.Id == id) {
            f.IsSelectedForSelection = checked;
          }
        })
      }
      else {
        this.estimateDetails.EstimateDetailsSections[sectionIndex].SubEstimateDetails.forEach(f => {
          if (f.Id == id) {
            f.IsSelectedForSelection = checked;
          }
        })
      }
    }
  }

  unselectSectionOptions(sectionIndex: number) {
    this.estimateDetails.EstimateDetailsSections[sectionIndex].EstimateCostLineItems.forEach(f => {
      f.IsSelectedForSelection = false;
    })
    this.estimateDetails.EstimateDetailsSections[sectionIndex].SubEstimateDetails.forEach(f => {
      f.IsSelectedForSelection = false;
    })
  }

  lineItemCheckbox(event: Event, displayIndex: number, sectionIndex: number) {
    if (this.status != this.estimateStatusTypes.Approved) {
      const target = event.target as HTMLInputElement;

      if (this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionType != EstimateSelectionTypeEnum.OptionalMultipleSelection) {
        this.unselectSectionOptions(sectionIndex);
        this.selectSectionSingleOption(displayIndex, sectionIndex, target.checked);
      }
      if (this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionType == EstimateSelectionTypeEnum.OptionalMultipleSelection) {
        this.selectSectionSingleOption(displayIndex, sectionIndex, target.checked);
      }

      this.calculateTotals();

      if (this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionType != EstimateSelectionTypeEnum.OptionalMultipleSelection) {
        this.estimateDetails.EstimateDetailsSections[sectionIndex].NoneSelected = !this.estimateDetails.EstimateDetailsSections[sectionIndex].displayList.some(item => item.IsSelectedForSelection);
      }
      if (this.estimateDetails.EstimateDetailsSections[sectionIndex].SelectionType == EstimateSelectionTypeEnum.OptionalMultipleSelection) {
        this.multiSelectionSteps(sectionIndex);
      }

      this.updateOptions();
    }
  }

  removedSections: any[];
  // normalSections: any[];
  reOrderSectionsWithSelection() {

    let sections = cloneDeep((this.estimateDetails.EstimateDetailsSections || []));
    sections = this.orderSections(sections);
    this.estimateDetails.EstimateDetailsSections = [];
    this.removedSections = [];

    if (sections.some(f => f.IsDisplayOnly)) {
      sections.filter(f => f.IsDisplayOnly).forEach(f => this.removedSections.push(f));
    }

    sections.filter(f => !f.IsDisplayOnly).forEach(f => this.estimateDetails.EstimateDetailsSections.push(f));
  }

  private sortSubsectionsItems(s: any) {
    if (s.SubEstimateDetails?.length) {
      s.SubEstimateDetails.map((subEstimate: any) => {
        subEstimate.EstimateCostLineItems = orderBy((subEstimate.EstimateCostLineItems || []), (d => d.Order), "asc")
      });
    }
  }

  logStackDataInSessionStack() {

    let CustomerFirstName = (this.estimate?.CustomerFirstName || this.customerPlaceHolderData?.CustomerFirstName || "");
    let CustomerLastName = (this.estimate?.CustomerLastName || this.customerPlaceHolderData?.CustomerLastName || "");
    let CustomerEmail = (this.estimate?.CustomerEmail || this.customerPlaceHolderData?.CompanyEmail || "");

    const sessionStackData = {
      userId: window.location.href,
      email: "" + CustomerEmail,
      displayName: "" + (CustomerFirstName + " " + CustomerLastName),

      customerFirstName_str: "" + CustomerFirstName,
      customerLastName_str: "" + CustomerLastName,
      customerCompanyName_str: "" + (this.customerPlaceHolderData?.CompanyName || ""),
      customerEmail_str: "" + CustomerEmail,
      customerPhoneNumber_str: "" + this.formatNumberForPreivew(this.estimate?.CustomerPhone || this.customerPlaceHolderData?.CustomerPhone || ""),
      estimateNumber_str: "" + (this.estimateDetails?.EstimateNumber || ""),
      estimateSubtotalEstimateValue_str: "" + (this.estimate?.EstimateValues?.TotalWithoutTax || 0),
      estimateCompanyName_str: "" + (this.estimate?.CompanyName || ""),
      estimateEmail_str: "" + (this.estimate?.CompanyEmail || ""),
      fromPreview_bool: this.isPreview,
    };

    this.logRocketService.initializeLogRocket();
    this.logRocketService.identify(sessionStackData);
  }

  // formate Phone Number
  formatNumberForPreivew(phoneNo: any) {
    try {
      const phoneNumber = parsePhoneNumber(phoneNo, 'US')
      return phoneNumber.formatNational()

    }
    catch {
      return phoneNo
    }
  }

  buttonStateMaintain(status: EstimateStatusTypes) {
    this.isClosed = status === EstimateStatusTypes.Closed;

    // A paid estimate takes no further answer, the same rule the server applies.
    if (this.isClosed || status === EstimateStatusTypes.CompletelyPaid) {
      this.showAcceptAndSign = false;
      this.showRequestRevision = false;
      this.showReject = false;
      this.status = status;
      this.isCollapsed = false;
      return;
    }

    if (!this.estimateDetails.AcceptedRejectedRevisionBy || this.estimateDetails.AcceptedRejectedRevisionBy == this.acceptedRejectedRevisionBy.Company) {
      this.showAcceptAndSign = true;
      this.showRequestRevision = true;
      this.showReject = true;
    } else {
      switch (status) {
        case EstimateStatusTypes.Approved:
          this.showAcceptAndSign = false;
          this.showRequestRevision = false;
          this.showReject = false;
          break;

        case EstimateStatusTypes.RevisionRequested:
          this.showAcceptAndSign = true;
          this.showRequestRevision = false;
          this.showReject = true;
          break;

        case EstimateStatusTypes.Rejected:
          this.showAcceptAndSign = true;
          this.showRequestRevision = true;
          this.showReject = false;
          break;

        default:
          this.showAcceptAndSign = true;
          this.showRequestRevision = true;
          this.showReject = true;
          break;
      }
    }

    this.status = status;
    this.isCollapsed = false;
  }

  showContent(type: EstimateStatusTypes) {
    this.isCollapsed = !this.isCollapsed;
    switch (type) {
      case EstimateStatusTypes.Approved:
        this.showAcceptAndSign = true;
        this.showRequestRevision = false;
        this.showReject = false;
        break;
      case EstimateStatusTypes.RevisionRequested:
        this.showAcceptAndSign = false;
        this.showRequestRevision = true;
        this.showReject = false;
        break;
      case EstimateStatusTypes.Rejected:
        this.showAcceptAndSign = false;
        this.showRequestRevision = false;
        this.showReject = true;
        break;
    }
  }

  /** Download filename shape: Estimate_{number}_{date}_{client}_{project}.pdf */
  generateDownloadFilename(): string {
    const parts: string[] = [];

    // Get current date in yyyy-mm-dd format
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const documentNumberTitle = this.estimateDetails?.Title || '';

    let estimateTitle = '';
    if (this.estimateDetails.EstimateType == EstimateDetailTypeEnum.ChangeOrder) {
      estimateTitle = 'Change_Order';
    }
    else {
      estimateTitle = this.estimate?.EstimateAlias || 'Estimate';
    }



    if (documentNumberTitle) {
      estimateTitle = documentNumberTitle.replace(/[<>:"/\\|?* ]/g, '_').trim();
    }
    // Build filename parts
    parts.push(estimateTitle.replace(/\s+/g, '_'));

    // 2. Document Number
    if (this.estimateDetails?.EstimateNumber ?? '') {
      parts.push(this.estimateDetails?.EstimateNumber);
    }

    // 3. Date
    parts.push(dateStr);

    // 5. Project name (if available)
    const projectName = this.estimate?.ProjectName ||
      this.projectPlaceHolderData?.ProjectName || '';
    if (projectName?.trim()) {
      parts.push(projectName.trim().replace(/\s+/g, '_'));
    }
    else {
      // 4. Customer name (if available)
      let clientName = getCustomerNameWithCompanyForEstInv(this.estimate);
      if (!clientName) {
        clientName = this.customerPlaceHolderData ? getCustomerNameWithCompany(this.customerPlaceHolderData) : "";
      }
      if (clientName) {
        parts.push(clientName.replace(/\s+/g, '_'));
      }
    }

    // Join parts with underscore and sanitize
    const filename = parts.join('_');
    return filename.replace(/[^a-zA-Z0-9_\-()]/g, '_') + '.pdf';
  }

  handlePrintPDF() {
    // Generate custom filename
    const customFilename = this.generateDownloadFilename();

    // Store original title
    const originalTitle = document.title;

    // Set document title to custom filename (without .pdf extension)
    document.title = customFilename.replace('.pdf', '');

    // Trigger print dialog
    window.print();

    // Restore original title after a short delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  }

  async submitResponse(formBody: UpdatePortalEstimateDTO) {
    await this.apiCallSubmit(formBody, EstimateStatusTypes.Approved);
  }

  afterResponseActions(event: EstimateApiStatusEmitter, isSuccess: boolean, msg: string) {
    this.isCollapsed = false;
    if (!event.IsLoader && !isSuccess) {
      this.showAcceptAndSign = false;
      this.showRequestRevision = false;
      this.showReject = false;
      this.actionText = msg;
      this.spinnerLabel = msg;
      this.showLoader = event.IsLoader;
      this.isErrorFromApi = true;
    }
    else {
      if (event.Status === EstimateStatusTypes.Approved) {
        this.showAcceptAndSign = false;
        this.actionText = `${this.typeText} Accepted`;
        this.spinnerLabel = 'Approval being sent';
        this.showLoader = event.IsLoader;
        this.status = EstimateStatusTypes.Approved;
        this.newlyStatusUpdated = true;
      }

      if (event.Status === EstimateStatusTypes.RevisionRequested) {
        this.actionText = 'Revision Requested';
        this.showLoader = event.IsLoader;
        this.spinnerLabel = 'Revision requested being sent';
        this.showRequestRevision = false;
        this.status = EstimateStatusTypes.RevisionRequested;
        this.newlyStatusUpdated = true;

      }

      if (event.Status === EstimateStatusTypes.Rejected) {
        this.actionText = 'Rejection sent';
        this.showLoader = event.IsLoader;
        this.spinnerLabel = 'Rejection being sent';
        this.showReject = false;
        this.status = EstimateStatusTypes.Rejected;
        this.newlyStatusUpdated = true;

      }
    }
  }

  async submitRevision() {
    let formValue = this.getFormData(this.requestRevisionForm);
    if (formValue) {
      if (this.isPreview) {
        this.alertService
          .confirm(
            'Are you sure you want to mark this estimate as revision for your client?'
          )
          .subscribe(async (response: any) => {
            if (response) {
              await this.apiCallSubmit(
                formValue as UpdatePortalEstimateDTO,
                EstimateStatusTypes.RevisionRequested
              );
            }
          });
      } else {
        await this.apiCallSubmit(
          formValue,
          EstimateStatusTypes.RevisionRequested
        );
      }
    }
  }

  async submitRejection() {
    let formValue = this.getFormData(this.rejectForm);
    if (formValue) {
      if (this.isPreview) {
        this.alertService
          .confirm(
            'Are you sure you want to mark this estimate as rejected for your client?'
          )
          .subscribe(async (response: any) => {
            if (response) {
              await this.apiCallSubmit(
                formValue as UpdatePortalEstimateDTO,
                EstimateStatusTypes.Rejected
              );
            }
          });
      } else {
        await this.apiCallSubmit(formValue, EstimateStatusTypes.Rejected);
      }
    }
  }

  async apiCallSubmit(
    formValue: UpdatePortalEstimateDTO,
    status: EstimateStatusTypes
  ) {
    this.loaderFunction(status, true);
    let apiResponse = await this.portalUrlService.updatePortalEstimate(
      formValue
    );
    if (apiResponse) {
      this.loaderFunction(status, false, apiResponse.IsSuccessful, apiResponse.Response);
      this.apiSuccessResponse = true;
    }
  }

  loaderFunction(status: EstimateStatusTypes, isLoader: boolean, isSuccess: boolean = true, msg: string = '') {
    this.status = EstimateStatusTypes.Draft;
    let body = {} as EstimateApiStatusEmitter;
    body.Status = status;
    body.IsLoader = isLoader;
    this.afterResponseActions(body, isSuccess, msg);
  }


  orderSections(sections: EstimateDetailsSectionEntityDTO[]) {
    return orderBy(sections, x => x.SectionOrder, ['asc']);
  }

  orderSectionCostlines(costlines: any[]) {
    return _.orderBy(costlines, d => d.SectionWiseOrder);
  }

  private getFormData(form: FormGroup) {
    Object.keys(form?.controls).forEach((key) => {
      form?.controls[key].markAsDirty();
    });

    if (form.invalid) {
      this.nameError = true;
      return;
    }

    let formValue = form.value as UpdatePortalEstimateDTO;
    formValue.PortalUrlId = this.portalEstimate.UrlId;
    formValue.EstimateDetailsId = this.estimateDetails.Id;
    formValue.EstimateAmount = this.estimate.EstimateValues?.TotalWithTax - (this.estimate.EstimateValues?.TotalDiscount || 0);
    formValue.CustomerName = `${this.estimate.CustomerFirstName} ${this.estimate.CustomerLastName}`;

    return formValue;
  }

  isEmpty(obj: any) {
    return !obj || objectIsEmpty(obj);
  }


  //#region update option sections

  updateOptions() {
    if (!this.selectionsLocked) {
      let data: UpdateSelectionOptionDTO = {
        EstId: this.estimate.Id,
        EstDetailId: this.estimateDetails.Id,
        PortalUrlId: this.portalEstimate.UrlId,
        OptionList: [],
      };

      (this.estimateDetails.EstimateDetailsSections || []).forEach(s => {
        if (s.IsSelectionEnabled && !s.IsDisplayOnly) {
          (s.EstimateCostLineItems || []).filter(f => f.IsSelectedForSelection).map(j => data.OptionList.push({ Id: j.Id, IsSection: false }));
          (s.SubEstimateDetails || []).filter(f => f.IsSelectedForSelection).map(j => data.OptionList.push({ Id: j.Id, IsSection: true }))
        }
      });

      this.updateOptionsSubject.next(data);
    }
  }

  private isProcessing: boolean = false;


  async submitOpdateOptions(action: UpdateSelectionOptionDTO) {
    this.isProcessing = true;
    const apiResponse = await this.portalUrlService.UpdateSelectionOption(action);
    this.isProcessing = false;
    if (!apiResponse?.IsSuccessful) {
      alert(apiResponse?.Response);
      this.globalStateService.setClientPortalRelatedDataChanges();
    }
  }

  noteListImages: any = []
  openImageInFullScreen(item: any, itemList: any) {
    this.noteListImages = itemList
    // this.photoSwipe.openImage(0, item);
    // this.globalStateService.setOpenLightboxObservable({Index:item.Id,Image: item,isGallery: true})

    this.globalStateService.setOpenLightboxForNoteObservable(this.noteListImages)
    let index = this.noteListImages?.findIndex((f: any) => f.Id === item.Id);
    this.globalStateService.setOpenLightboxObservable({ Index: index, Image: item, isGallery: true, isNoteList: false })

  }
  //#endregion

  checkIfAnyItemSelected(displayList: any[]) {
    return (displayList || []).some(g => g.IsSelectedForSelection);
  }

  /**
   * Selection labels and unselected options follow estimateDetails.ShowUnselectedInPrint, set
   * per document by the editor's "Show Unselected" toggle. The client cannot change it.
   */
  getSelectionLabel(displayList: any[], selectionType: number): string {
    const total = (displayList || []).length;
    const selected = (displayList || []).filter(x => x.IsSelectedForSelection).length;
    if (selectionType === this.estimateSelectionType.OptionalMultipleSelection) {
      if (selected === 0) {
        return `Select any combination of the following ${total} option${total !== 1 ? 's' : ''}`;
      }
      return `${selected} of ${total} option${total !== 1 ? 's' : ''} selected`;
    }
    if (selectionType === this.estimateSelectionType.OptionalSingleSelection) {
      if (selected === 0) {
        return `Choose one of the following ${total} option${total !== 1 ? 's' : ''}, or decline all`;
      }
      return `Choose one \u2014 recommended option included in total`;
    }
    // Required single selection
    if (selected === 0) {
      return `Choose one of the following ${total} option${total !== 1 ? 's' : ''}`;
    }
    return `Choose one \u2014 selected option included in total`;
  }

  hasAnySelected(displayList: any[]): boolean {
    return (displayList || []).some(x => x.IsSelectedForSelection);
  }

  checkIfAnyItemFilesExists(item: any) {
    return (!item.IsSelectionEnabled && (item.ItemsFiles || []).length > 0) ||
      item.IsSelectionEnabled && item.displayList.some((f: any) => (f.ItemsFiles || []).length > 0);
  }

  checkNextIsSectionOrCostline(index: number, displayList: any[], type: any) {
    let indexFlag = index + 1;
    let flag = false;
    while (indexFlag <= displayList.length) {
      if (displayList[indexFlag] && displayList[indexFlag].renderedType === type) {
        flag = true;
        break;
      }
      indexFlag++;
    }
    return flag;
  }

  checkIfPreviousIsSubsection(index: number, displayList: any[]) {
    let indexFlag = index;
    let flag = false;
    while (indexFlag > 0) {
      if (displayList[indexFlag - 1] && displayList[indexFlag - 1].renderedType === this.renderTypes.subsection) {
        flag = true;
        break;
      }
      indexFlag--;
    }

    return flag;
  }

  /**
   * Counts only dollars a published document has not already made official: lines tied to
   * unpublished or rejected documents, plus additional lines linked to no document at all.
   */
  private async calculateInvoiceContribution(invoices: any[], publishedDetails: any[], allPreviousDetails: any[]): Promise<number> {
    if (!invoices || invoices.length === 0) {
      return 0;
    }

    let totalContribution = 0;
    // Get IDs of published estimate details for comparison
    const publishedDetailIds = new Set(publishedDetails.map(d => d.Id));
    let isCurrentPublished = [EstimateStatusTypes.Approved, EstimateStatusTypes.PendingApproval, EstimateStatusTypes.Overdue, EstimateStatusTypes.RevisionRequested, EstimateStatusTypes.CompletelyPaid].includes(this.estimateDetails.Status);
    let isCurrentNotRejected = this.estimateDetails.Status != EstimateStatusTypes.Rejected;

    if (isCurrentPublished && isCurrentNotRejected) {
      publishedDetailIds.add(this.estimateDetails.Id);
    }

    // Track which line items we've already counted to avoid double-counting
    const countedLineItems = new Set<string>();

    let totalTaxAmount = 0;
    let qualifyingInvoiceValue = 0;
    let qualifyingAlreadyTaxedAmount = 0; // Track items that already have tax applied (LineItemTax or TaxId)

    const processLineItem = (lineItem: any) => {
      qualifyingInvoiceValue += (lineItem.InvoicedPrice || lineItem.Price || 0);

      let taxData = this.getLineItemTaxAmount(lineItem);
      totalTaxAmount += taxData.taxAmount;
      qualifyingAlreadyTaxedAmount += taxData.qualifyingAlreadyTaxedAmount;
    }

    for (const invoice of invoices) {


      invoice.InvoiceDiscounts = (invoice.InvoiceDiscounts || []).map((d: any) => ({ ...d, ...d?.DiscountEntity }));

      const lineItems = await this.invoiceService.estimatedLineItemsMapping({ ...this.estimate, EstimateDetails: allPreviousDetails }, invoice, invoiceTaxesPerLine(invoice));
      invoice.EstimatedLineItems = lineItems;
      this.invoiceService.calculateInvoiceTotalValues(invoice);

      totalTaxAmount = 0;
      qualifyingInvoiceValue = 0;
      qualifyingAlreadyTaxedAmount = 0;

      for (const lineItem of (invoice.EstimatedLineItems || [])) {
        const lineItemKey = `${lineItem.Id || ''}_${lineItem.CostLineId || ''}_${lineItem.SectionId || ''}`;

        if (countedLineItems.has(lineItemKey)) {
          continue;
        }

        countedLineItems.add(lineItemKey);


        // Include line items from unpublished/rejected documents
        if (lineItem.EstimateDetailId) {

          if (!publishedDetailIds.has(lineItem.EstimateDetailId)) {
            processLineItem(lineItem);
          }

        }
        else if (lineItem.CostLineId || lineItem.SectionId) {
          // Handle EstimatedLineItems without EstimateDetailsId by checking if they belong to unpublished details
          const belongsToUnpublishedDetail =
            this.checkIfLineItemBelongsToUnpublishedDetails(
              lineItem, allPreviousDetails, publishedDetailIds
            );

          if (belongsToUnpublishedDetail) {
            processLineItem(lineItem);
          }

        }

      }

      for (const additionalItem of (invoice.AdditionalLineItems || [])) {
        const lineItemKey = `additional_${additionalItem.Id || ''}_${invoice.Id}`;

        if (countedLineItems.has(lineItemKey)) {
          continue;
        }

        // Additional items not linked to estimates should always be included
        if (!additionalItem.EstimateDetailsId || additionalItem.EstimateDetailsId === null) {
          processLineItem(additionalItem);
        }
        else if (!publishedDetailIds.has(additionalItem.EstimateDetailsId)) {
          processLineItem(additionalItem);
        }
        countedLineItems.add(lineItemKey);

      }

      let preTaxDiscount = 0;
      let postTaxDiscount = 0;

      for (const discount of (invoice.InvoiceDiscounts || [])) {
        if (discount.ApplyBeforeTax) {
          preTaxDiscount += (discount.DiscountedValue || 0);
        }
        else {
          postTaxDiscount += (discount.DiscountedValue || 0);
        }
      }

      // Apply pre-tax discounts as absolute amounts                 
      qualifyingInvoiceValue -= preTaxDiscount;
      qualifyingInvoiceValue = qualifyingInvoiceValue < 0 ? 0 : qualifyingInvoiceValue;


      // Only the value this invoice actually contributed carries invoice-level tax. Taxing the
      // whole TaxableSubTotal charges the baseline for tax on already-published work it has
      // counted once already, which a later change order re-triggers on every older one.
      if (invoice.Tax && invoice.Tax.Percent > 0) {
        const invoiceTaxableCeiling = invoice.TaxableSubTotal > 0 ? invoice.TaxableSubTotal : 0;
        const qualifyingTaxableAmt = Math.min(
          Math.max(qualifyingInvoiceValue - qualifyingAlreadyTaxedAmount, 0),
          invoiceTaxableCeiling
        );

        if (qualifyingTaxableAmt > 0) {
          totalTaxAmount += (qualifyingTaxableAmt * invoice.Tax.Percent) / 100;
        }
      }

      qualifyingInvoiceValue += totalTaxAmount;

      // Apply post-tax discounts as absolute amounts          
      qualifyingInvoiceValue -= postTaxDiscount;
      qualifyingInvoiceValue = qualifyingInvoiceValue < 0 ? 0 : qualifyingInvoiceValue;

      totalContribution += qualifyingInvoiceValue;

    }

    return totalContribution;
  }

  private checkIfLineItemBelongsToUnpublishedDetails(
    lineItem: any,
    allPreviousDetails: any[],
    publishedDetailIds: Set<string>
  ): boolean {
    // Get unpublished details (those not in publishedDetailIds)
    const unpublishedDetails = allPreviousDetails.filter(detail =>
      !publishedDetailIds.has(detail.Id)
    );

    // Check if the line item's CostLineId or SectionId exists in any unpublished detail
    for (const detail of unpublishedDetails) {
      if (detail.EstimateDetailsSections) {
        for (const section of detail.EstimateDetailsSections) {
          // Check if SectionId matches
          if (lineItem.SectionId && section.Id === lineItem.SectionId) {
            return true;
          }

          // Check if CostLineId matches any cost line in this section
          if (lineItem.CostLineId && section.EstimateCostLineItems) {
            for (const costLine of section.EstimateCostLineItems) {
              if (costLine.Id === lineItem.CostLineId) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }

  private getLineItemTaxAmount(lineItem: any): { taxAmount: number, qualifyingAlreadyTaxedAmount: number } {
    let taxData: {
      taxAmount: number,
      qualifyingAlreadyTaxedAmount: number
    } = {
      taxAmount: 0,
      qualifyingAlreadyTaxedAmount: 0
    };

    // Handle individual tax on  line items
    if (lineItem.LineItemTax || lineItem.LineItemTaxAmount) {
      // Direct tax amount specified on line item
      taxData.taxAmount = (lineItem.LineItemTax || lineItem.LineItemTaxAmount || 0);
      // Track this amount as already taxed to exclude from invoice-level tax
      taxData.qualifyingAlreadyTaxedAmount = (lineItem.InvoicedPrice || 0);

    } else if (lineItem.TaxId && lineItem.IsTaxable) {

      // Calculate tax using TaxId and tax rate
      const taxRate = this.portalEstimate.Item.Taxes.find((tax: any) => tax.Id === lineItem.TaxId);
      if (taxRate && taxRate.Percent) {
        taxData.taxAmount = ((lineItem.InvoicedPrice || 0) * taxRate.Percent) / 100;
        // Track this amount as already taxed to exclude from invoice-level tax
        taxData.qualifyingAlreadyTaxedAmount = (lineItem.InvoicedPrice || 0);
      }

    }

    return taxData;
  }

  private tryGetISODate(date: Date) {
    try {
      return date.toISOString();
    } catch (error) {
      return date;
    }
  }

  private calculateRates() {
    (this.estimateDetails.EstimateDetailsSections || []).forEach(section => {
      this.cacheRatesForSection(section);
    });
  }

  private cacheRatesForSection(section: any) {
    // Cache rates for direct costlines
    (section.EstimateCostLineItems || []).forEach((costline: any) => {
      costline.CalculatedRate = this.calculateRateForCostline(costline);
    });

    // Cache rates for subsection costlines
    (section.SubEstimateDetails || []).forEach((subsection: any) => {
      (subsection.EstimateCostLineItems || []).forEach((costline: any) => {
        costline.CalculatedRate = this.calculateRateForCostline(costline);
      });
    });
  }

  /**
   * A share link that withholds cost and markup states the rate; one that still carries them falls back to
   * cost per unit and margin/markup when quantity is 0 and cannot be divided by.
   */
  private calculateRateForCostline(costline: any): number {
    if (typeof costline.CustomerUnitPrice === 'number') {
      return costline.CustomerUnitPrice;
    }

    // If quantity > 0, use the standard calculation
    if ((costline.DefaultQty || 0) > 0) {
      return costline.calculatedCustomerCost > 0 ?
        (costline.calculatedCustomerCost) / (costline.DefaultQty) : 0;
    }

    // If quantity is 0, calculate rate from cost per unit + margin/markup
    const costPerUnit = costline.DefaultCostPer || 0;
    const markupValue = costline.DefaultMarkupPer || 0;
    const markupType = costline.DefaultMarkupType;
    const isMargin = costline.IsMargin || false;

    if (costPerUnit === 0) {
      return 0;
    }

    let rate = 0;

    // Check if markup/margin is a dollar value (MarkupEnumType.Amount) or percentage
    if (markupType === '$' || markupType === 'Amount') {
      // Dollar value: rate = costPerUnit + markup
      rate = costPerUnit + markupValue;
    } else {
      // Percentage value
      if (isMargin) {
        // Margin: rate = costPerUnit / (1 - margin/100)
        const marginDecimal = markupValue / 100;
        if (marginDecimal >= 1) {
          rate = costPerUnit; // Avoid division by zero or negative
        } else {
          rate = costPerUnit / (1 - marginDecimal);
        }
      } else {
        // Markup: rate = costPerUnit * (1 + markup/100)
        rate = costPerUnit * (1 + markupValue / 100);
      }
    }

    return rate;
  }

  getTotalFontSize(value: number): string {
    const abs = Math.abs(value || 0);
    const formatted = '$' + abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const str = (value < 0 ? '-' : '') + formatted;
    if (str.length > 14) return '12px';
    if (str.length > 12) return '14px';
    return '16px';
  }

}
